import { SquadProfileDialog } from './PlayerProfileAccess';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Avatar,
  AvatarFallback,
  AvatarImage
} from '../ui/avatar';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import {
  Users,
  Edit,
  ArrowUp,
  ArrowDown,
  Filter,
  ChevronRight,
  MessageSquare,
  Phone,
  Mail,
  MessageCircle,
  Search,
  X
} from 'lucide-react';
import { getInitials } from '../../lib/utils';
import { resolveAvatarUrl } from '../../lib/avatar';

const POSITION_COLORS = {
  'Målvakt': "bg-yellow-500/10 text-[rgb(var(--tn-text-fde047))] border-yellow-500/25",
  'Back': "bg-blue-500/10 text-[rgb(var(--tn-text-93c5fd))] border-blue-500/25",
  'Mittfält': "bg-emerald-500/10 text-[rgb(var(--tn-text-6ee7b7))] border-emerald-500/25",
  'Anfall': "bg-rose-500/10 text-[rgb(var(--tn-text-fda4af))] border-rose-500/25"
};

function getBirthMeta(value) {
  if (!value) {
    return null;
  }

  const raw = String(value).trim();

  if (!raw) {
    return null;
  }

  const date = new Date(
    raw.includes('T')
      ? raw
      : `${raw.slice(0, 10)}T12:00:00`
  );

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const today = new Date();

  let age =
    today.getFullYear() -
    date.getFullYear();

  const monthDiff =
    today.getMonth() -
    date.getMonth();

  if (
    monthDiff < 0 ||
    (
      monthDiff === 0 &&
      today.getDate() < date.getDate()
    )
  ) {
    age -= 1;
  }

  return {
    age,
    year: date.getFullYear()
  };
}

function PlayerContactActions({
  player,
  teamId,
  onStartDM
}) {
  const { t } = useTranslation('team_admin');

  const phone =
    player.phone?.replace(/\s+/g, '');

  const email = player.email;

  const isMobile =
    typeof window !== 'undefined' &&
    window.innerWidth < 768;

  if (!isMobile) {
    return null;
  }

  return (
    <div className="mt-2 flex gap-1.5 border-t border-[rgb(var(--tn-border-ffffff))]/[0.06] pt-2">

      {player.user_id && onStartDM && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 flex-1 text-xs"
          onClick={(event) => {
            event.stopPropagation();

            onStartDM(
              teamId,
              player.user_id
            );
          }}
        >
          <MessageSquare className="mr-1 h-3.5 w-3.5" />
          {t('roster.chat')}
        </Button>
      )}

      {phone && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3"
          asChild
          onClick={(event) =>
            event.stopPropagation()
          }
        >
          <a href={`tel:${phone}`}>
            <Phone className="h-3.5 w-3.5" />
          </a>
        </Button>
      )}

      {phone && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3"
          asChild
          onClick={(event) =>
            event.stopPropagation()
          }
        >
          <a href={`sms:${phone}`}>
            <MessageCircle className="h-3.5 w-3.5" />
          </a>
        </Button>
      )}

      {email && (
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3"
          asChild
          onClick={(event) =>
            event.stopPropagation()
          }
        >
          <a href={`mailto:${email}`}>
            <Mail className="h-3.5 w-3.5" />
          </a>
        </Button>
      )}

    </div>
  );
}

function PlayerCard({
  player,
  onEdit,
  canEdit,
  onViewProfile,
  teamId,
  onStartDM
}) {
  const { t } = useTranslation('team_admin');

  const birthMeta = getBirthMeta(
    player.birth_date ||
    player.birthdate
  );

  const hasPosition =
    Boolean(player.position);

  const positionColor =
    POSITION_COLORS[player.position] ||
    "bg-[rgb(var(--tn-tint-ffffff))]/[0.035] text-[rgb(var(--tn-text-a8b0ba))] border-[rgb(var(--tn-border-ffffff))]/[0.08]";

  return (
    <Card
      className="group cursor-pointer rounded-lg border-[rgb(var(--tn-border-ffffff))]/[0.065] bg-[rgb(var(--tn-bg-151a20))] shadow-none transition-colors hover:border-[rgb(var(--tn-border-ffffff))]/[0.12] hover:bg-[rgb(var(--tn-bg-191f26))]"
      onClick={() =>
        onViewProfile(player.id)
      }
      data-testid={`player-card-${player.id}`}
    >
      <CardContent className="p-2">

        <div className="flex min-w-0 items-center gap-2.5">

          <div className="relative shrink-0">

            <Avatar className="h-10 w-10 border border-[rgb(var(--tn-border-ffffff))]/[0.09] bg-[rgb(var(--tn-bg-20262d))]">

              {player.avatar_url && (
                <AvatarImage
                  src={resolveAvatarUrl(
                    player.avatar_url
                  )}
                  alt={
                    player.name ||
                    'Spelare'
                  }
                  className="object-cover"
                />
              )}

              <AvatarFallback className="bg-[rgb(var(--tn-bg-20262d))] text-[12px] font-semibold text-[var(--t-accent)]">
                {getInitials(
                  player.name
                )}
              </AvatarFallback>

            </Avatar>

            {player.jersey_number !== null &&
              player.jersey_number !== undefined &&
              player.jersey_number !== '' && (
                <span className="absolute -bottom-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-[rgb(var(--tn-border-151a20))] bg-[var(--tn-accent-fill)] px-1 text-[9px] font-bold leading-none text-[#111318]">
                  {player.jersey_number}
                </span>
              )}

          </div>

          <div className="min-w-0 flex-1">

            <p
              className="truncate text-[13px] font-semibold leading-4 text-[rgb(var(--tn-text-f2f4f7))]"
              title={player.name}
            >
              {player.name}
            </p>

            {birthMeta && (
              <p className="mt-0.5 truncate text-[10.5px] leading-4 text-[rgb(var(--tn-text-87919d))]">
                {birthMeta.age} år ({birthMeta.year})
              </p>
            )}

            <div
              className={
                birthMeta
                  ? 'mt-1 flex min-h-5 min-w-0 items-center'
                  : 'mt-0.5 flex min-h-5 min-w-0 items-center'
              }
            >
              {hasPosition ? (
                <Badge
                  variant="outline"
                  className={`${positionColor} h-5 max-w-full rounded-md px-1.5 text-[9.5px] font-medium leading-none`}
                >
                  <span className="truncate">
                    {player.position}
                  </span>
                </Badge>
              ) : (
                <span className="truncate text-[9.5px] font-medium text-[rgb(var(--tn-text-66717d))]">
                  {t('roster.no_position')}
                </span>
              )}
            </div>

          </div>

          <div className="flex shrink-0 items-center gap-0.5">

            {canEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-md text-[rgb(var(--tn-text-8d97a3))] hover:bg-[rgb(var(--tn-tint-ffffff))]/[0.05] hover:text-[rgb(var(--tn-text-ffffff))]"
                data-testid={`edit-member-${player.id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(player);
                }}
              >
                <Edit className="h-3.5 w-3.5" />
              </Button>
            )}

            <ChevronRight className="h-3.5 w-3.5 text-[rgb(var(--tn-text-66717d))] transition-transform group-hover:translate-x-0.5 group-hover:text-[rgb(var(--tn-text-c5ccd4))]" />

          </div>

        </div>

        <PlayerContactActions
          player={player}
          teamId={teamId}
          onStartDM={onStartDM}
        />

      </CardContent>
    </Card>
  );
}

export default function PlayersList({
  players,
  loading,
  sortOption,
  onSortChange,
  filterOption,
  onFilterChange,
  onEditMember,
  canEdit,
  totalCount,
  teamId,
  onStartDM,
  showSectionTitle = true
}) {
  const { t } = useTranslation(
    'team_admin'
  );

  const navigate = useNavigate();
  const [previewPlayerId, setPreviewPlayerId] = useState(null);

  const [
    searchQuery,
    setSearchQuery
  ] = useState('');

  const PLAYER_SORT_OPTIONS = [
    {
      id: 'position_asc',
      label: t('sort.position_asc'),
      icon: ArrowUp
    },
    {
      id: 'position_desc',
      label: t('sort.position_desc'),
      icon: ArrowDown
    },
    {
      id: 'jersey_asc',
      label: t('sort.jersey_asc'),
      icon: ArrowUp
    },
    {
      id: 'jersey_desc',
      label: t('sort.jersey_desc'),
      icon: ArrowDown
    },
    {
      id: 'name_asc',
      label: t('sort.name_asc'),
      icon: ArrowUp
    },
    {
      id: 'name_desc',
      label: t('sort.name_desc'),
      icon: ArrowDown
    }
  ];

  const POSITION_FILTERS = [
    {
      id: 'all',
      label: t('positions.all')
    },
    {
      id: 'goalkeeper',
      label: t(
        'positions.goalkeeper'
      )
    },
    {
      id: 'defender',
      label: t(
        'positions.defender'
      )
    },
    {
      id: 'midfielder',
      label: t(
        'positions.midfielder'
      )
    },
    {
      id: 'forward',
      label: t(
        'positions.forward'
      )
    }
  ];

  const visiblePlayers = useMemo(
    () => {
      const query =
        searchQuery
          .trim()
          .toLocaleLowerCase(
            'sv-SE'
          );

      if (!query) {
        return players;
      }

      return players.filter(
        (player) => {
          const searchable = [
            player.name,
            player.position,
            player.jersey_number
          ]
            .filter(
              (value) =>
                value !== null &&
                value !== undefined
            )
            .join(' ')
            .toLocaleLowerCase(
              'sv-SE'
            );

          return searchable.includes(
            query
          );
        }
      );
    },
    [
      players,
      searchQuery
    ]
  );

  const handleViewProfile = (
    playerId
  ) => {
    if (canEdit) {
      navigate(`/players/${playerId}`);
    } else {
      setPreviewPlayerId(playerId);
    }
  };

  const SortIcon =
    PLAYER_SORT_OPTIONS.find(
      (option) =>
        option.id === sortOption
    )?.icon ||
    ArrowUp;

  const isFiltering =
    filterOption !== 'all';

  const isSearching =
    Boolean(
      searchQuery.trim()
    );

  if (loading) {
    return (
      <section className="tn-panel overflow-hidden rounded-xl border border-[rgb(var(--tn-border-ffffff))]/[0.07] bg-[rgb(var(--tn-bg-12161b))] shadow-none">

        <div className={`tn-section-header flex min-h-[52px] items-center border-b border-[rgb(var(--tn-border-ffffff))]/[0.07] px-3.5 ${
          showSectionTitle ? 'justify-between' : 'justify-end'
        }`}>

          {showSectionTitle && (
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-7 w-7 rounded-lg" />
              <Skeleton className="h-5 w-24" />
            </div>
          )}

          <Skeleton className="h-8 w-[440px] max-w-full" />

        </div>

        <div className="grid gap-2 p-2.5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">

          {[
            1,
            2,
            3,
            4,
            5,
            6,
            7,
            8
          ].map((item) => (
            <Skeleton
              key={item}
              className="h-[66px] rounded-lg"
            />
          ))}

        </div>

      </section>
    );
  }

  return (
    <section className="tn-panel overflow-hidden rounded-xl border border-[rgb(var(--tn-border-ffffff))]/[0.07] bg-[rgb(var(--tn-bg-12161b))] shadow-none">

      {/* Toolbar. In the Trupp page the page header already owns the
          Spelare/ Ledare identity, so we avoid repeating the section title. */}
      <div className={`tn-section-header flex min-h-[52px] flex-col gap-2 border-b border-[rgb(var(--tn-border-ffffff))]/[0.07] bg-[rgb(var(--tn-bg-12161b))] px-3 py-2 sm:flex-row sm:items-center ${
        showSectionTitle ? 'sm:justify-between' : 'sm:justify-end'
      }`}>

        {showSectionTitle && (
          <div className="flex shrink-0 items-center gap-2.5">

            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--tn-accent-fill)]/10 text-[var(--t-accent)]">
              <Users className="h-4 w-4" />
            </div>

            <h2 className="tn-section-title text-[14px] font-semibold text-[rgb(var(--tn-text-f2f4f7))]">
              {t('roster.players')}
            </h2>

            <span className="flex h-5 min-w-5 items-center justify-center rounded-md bg-[rgb(var(--tn-tint-ffffff))]/[0.055] px-1.5 text-[10px] font-semibold tabular-nums text-[rgb(var(--tn-text-c5ccd4))]">
              {isFiltering ||
              isSearching
                ? `${visiblePlayers.length}/${totalCount}`
                : players.length}
            </span>

          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:justify-end">

          <Select
            value={filterOption}
            onValueChange={
              onFilterChange
            }
          >
            <SelectTrigger
              className={`tn-control h-8 w-[156px] rounded-lg bg-[rgb(var(--tn-bg-0f1318))] px-2.5 text-[11px] shadow-none ${
                isFiltering
                  ? 'border-[var(--t-accent)]/35'
                  : "border-[rgb(var(--tn-border-ffffff))]/[0.08]"
              }`}
              data-testid="position-filter-select"
            >
              <Filter
                className={`mr-1.5 h-3.5 w-3.5 ${
                  isFiltering
                    ? 'text-[var(--t-accent)]'
                    : "text-[rgb(var(--tn-text-7f8995))]"
                }`}
              />

              <SelectValue />
            </SelectTrigger>

            <SelectContent>

              {POSITION_FILTERS.map(
                (option) => (
                  <SelectItem
                    key={option.id}
                    value={option.id}
                  >
                    {option.label}
                  </SelectItem>
                )
              )}

            </SelectContent>
          </Select>

          <div className="relative min-w-[180px] flex-1 sm:max-w-[250px]">

            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[rgb(var(--tn-text-747f8b))]" />

            <Input
              value={searchQuery}
              onChange={(event) =>
                setSearchQuery(
                  event.target.value
                )
              }
              placeholder={t(
                'roster.search_placeholder'
              )}
              className="tn-control h-8 rounded-lg border-[rgb(var(--tn-border-ffffff))]/[0.08] bg-[rgb(var(--tn-bg-0f1318))] pl-8 pr-8 text-[11px] shadow-none placeholder:text-[rgb(var(--tn-text-69737f))]"
              data-testid="player-search-input"
            />

            {isSearching && (
              <button
                type="button"
                onClick={() =>
                  setSearchQuery('')
                }
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[rgb(var(--tn-text-737e89))] transition-colors hover:text-[rgb(var(--tn-text-ffffff))]"
                aria-label="Rensa sökning"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}

          </div>

          <Select
            value={sortOption}
            onValueChange={
              onSortChange
            }
          >
            <SelectTrigger
              className="tn-control h-8 w-[205px] rounded-lg border-[rgb(var(--tn-border-ffffff))]/[0.08] bg-[rgb(var(--tn-bg-0f1318))] px-2.5 text-[11px] shadow-none"
              data-testid="player-sort-select"
            >
              <SortIcon className="mr-1.5 h-3.5 w-3.5 text-[rgb(var(--tn-text-7f8995))]" />
              <SelectValue />
            </SelectTrigger>

            <SelectContent>

              {PLAYER_SORT_OPTIONS.map(
                (option) => {
                  const Icon =
                    option.icon;

                  return (
                    <SelectItem
                      key={option.id}
                      value={option.id}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5" />
                        {option.label}
                      </div>
                    </SelectItem>
                  );
                }
              )}

            </SelectContent>
          </Select>

        </div>

      </div>

      {/* Player grid */}
      <div className="p-2.5">

        {visiblePlayers.length >
        0 ? (
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">

            {visiblePlayers.map(
              (player) => (
                <PlayerCard
                  key={player.id}
                  player={player}
                  onEdit={
                    onEditMember
                  }
                  canEdit={
                    canEdit
                  }
                  onViewProfile={
                    handleViewProfile
                  }
                  teamId={teamId}
                  onStartDM={
                    onStartDM
                  }
                />
              )
            )}

          </div>
        ) : (
          <div className="flex min-h-[110px] items-center justify-center text-center">

            <div>

              <Users className="mx-auto mb-2 h-5 w-5 text-[rgb(var(--tn-text-59636f))]" />

              <p className="text-[12px] text-[rgb(var(--tn-text-8d97a3))]">

                {isSearching
                  ? t(
                      'roster.empty_no_match'
                    )
                  : isFiltering
                    ? t(
                        'roster.no_players_filter'
                      )
                    : t(
                        'roster.no_players'
                      )}

              </p>

            </div>

          </div>
        )}

      </div>

      {previewPlayerId && <SquadProfileDialog
        playerId={previewPlayerId}
        teamId={teamId}
        onClose={() => setPreviewPlayerId(null)}
      />}
    </section>
  );
}